import { when } from "mobx";

/**
 * Wait until a lazily-fetched list has settled, so that writing one item into
 * its map is not silently thrown away.
 *
 * Extracted from EmailsStore because this is the third revision of that path
 * and the previous two were both wrong in ways no test could see. The store
 * itself cannot be unit tested here: importing it pulls in DomainStore, which
 * constructs every store, and the circular import fails outright. This
 * function takes the three things it needs as callbacks, so the rule can be
 * pinned even though its caller cannot be.
 *
 * Two traps, both of which this codebase has fallen into.
 *
 * Waiting on an `initialized` flag does not work. It is flipped synchronously
 * when the getter is first read, long before the fetch resolves, so the wait
 * returns while the map is still undefined and the caller's optional-chained
 * `set` does nothing at all.
 *
 * Waiting only on the map does not work either. The map is assigned in the
 * fetch's success path and nowhere else, so if the fetch fails the wait never
 * ends, and nothing retries. That turns a dropped write into a permanent hang
 * for every later caller, which is worse than the bug it replaced.
 *
 * So: wait for the map, OR for the fetch to have stopped running. The loading
 * flag is the honest "settled" signal precisely because it is cleared on both
 * success and failure. It must be observable, or `when` tracks nothing and
 * never resolves.
 */
export const awaitSettledList = async ({
  hasList,
  isLoading,
  startLoading,
}: {
  /** True once the list has actually been assigned. */
  hasList: () => boolean;
  /** True while the fetch is in flight. Must read an observable. */
  isLoading: () => boolean;
  /** Kick off the fetch if nothing has yet. Must be synchronous. */
  startLoading: () => void;
}): Promise<void> => {
  if (hasList()) {
    return;
  }
  startLoading();
  await when(() => hasList() || !isLoading());
};
